/**
 * Consentement cookies (RGPD).
 *
 * Aucun script d'analyse (Google Analytics, PostHog) n'est chargé avant un
 * consentement explicite. Le refus est mémorisé et respecté à chaque visite.
 * Le monitoring d'erreurs (Sentry, entièrement masqué, sans PII) reste actif
 * au titre de la sécurité du service.
 */

export type ConsentValue = 'granted' | 'denied';

const STORAGE_KEY = 'mtb-cookie-consent-v1';
export const CONSENT_EVENT = 'mtb-consent-change';
const GA_MEASUREMENT_ID = 'G-9FBT4PDBXJ';

type GtagFn = (...args: unknown[]) => void;

const getGtag = (): GtagFn | undefined =>
  (window as unknown as { gtag?: GtagFn }).gtag;

export function getConsent(): ConsentValue | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

let analyticsLoaded = false;

function loadGoogleAnalytics() {
  if (document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** Charge les scripts analytics — appelé uniquement si le consentement est accordé. */
export function loadAnalytics() {
  if (analyticsLoaded || typeof window === 'undefined') return;
  analyticsLoaded = true;

  const gtag = getGtag();
  if (gtag) {
    gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  }
  loadGoogleAnalytics();
  if (gtag) gtag('event', 'page_view');

  import('./posthog')
    .then((mod) => mod.initPostHog())
    .catch(() => {
      /* best-effort */
    });
}

export function setConsent(value: ConsentValue) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* stockage indisponible : le choix reste valable pour la session */
  }
  if (value === 'granted') {
    loadAnalytics();
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

/** À appeler au démarrage : réactive les scripts si l'utilisateur avait déjà accepté. */
export function initConsent() {
  if (getConsent() === 'granted') loadAnalytics();
}
